// ============================================================
// 天水數位店長 — 登入頁
// 莫蘭迪色系、極簡、iPad 觸控優化
//
// 簡化特性：
//   - 自動記住 Email（localStorage）
//   - 預設單店、進階設定才顯示店鋪代碼
//   - Enter 即可提交、Tab 依序跳欄
//   - 密碼顯示/隱藏切換
//   - JWT 90 天，幾乎不用重複登入
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

const LS_TENANT = 'tianshui_last_tenant';
const LS_EMAIL = 'tianshui_last_email';
const DEFAULT_TENANT = 'tianshui'; // 單店預設，自動帶入

export function LoginPage() {
  const [tenantSlug, setTenantSlug] = useState(DEFAULT_TENANT);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { login, isLoading, error } = useAuthStore();
  const navigate = useNavigate();

  // 載入上次登入資訊
  useEffect(() => {
    const t = localStorage.getItem(LS_TENANT);
    const e = localStorage.getItem(LS_EMAIL);
    if (t && t !== DEFAULT_TENANT) {
      setTenantSlug(t);
      setShowAdvanced(true); // 非預設店鋪 → 顯示進階欄位
    }
    if (e) setEmail(e);
  }, []);

  const doLogin = async (slug: string, em: string, pwd: string) => {
    await login(em, pwd, slug);
    const user = useAuthStore.getState().user;
    if (user) {
      localStorage.setItem(LS_TENANT, slug);
      localStorage.setItem(LS_EMAIL, em);
      navigate(user.role === 'STAFF' ? '/my' : '/dashboard');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await doLogin(tenantSlug, email, password);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-morandi-50 p-6">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-earth-200 rounded-2xl mb-4">
            <span className="text-2xl text-earth-600 font-bold">天</span>
          </div>
          <h1 className="text-2xl font-bold text-morandi-800 tracking-tight">天水數位店長</h1>
          <p className="text-sm text-morandi-400 mt-1">排班 · 打卡 · 營運</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {showAdvanced && (
            <input
              type="text"
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              placeholder="店鋪代碼"
              required
              autoComplete="organization"
              className="w-full px-4 py-3 bg-white border border-morandi-200 rounded-btn text-morandi-700
                         placeholder:text-morandi-300 focus:outline-none focus:ring-2 focus:ring-smoke-300
                         focus:border-transparent transition-all min-h-touch"
            />
          )}

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            autoComplete="email"
            className="w-full px-4 py-3 bg-white border border-morandi-200 rounded-btn text-morandi-700
                       placeholder:text-morandi-300 focus:outline-none focus:ring-2 focus:ring-smoke-300
                       focus:border-transparent transition-all min-h-touch"
          />

          <div className="relative">
            <input
              type={showPwd ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="密碼"
              required
              autoComplete="current-password"
              className="w-full px-4 py-3 pr-14 bg-white border border-morandi-200 rounded-btn text-morandi-700
                         placeholder:text-morandi-300 focus:outline-none focus:ring-2 focus:ring-smoke-300
                         focus:border-transparent transition-all min-h-touch"
            />
            <button
              type="button"
              onClick={() => setShowPwd(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-morandi-400 hover:text-morandi-600 px-2 py-1"
              tabIndex={-1}
            >
              {showPwd ? '隱藏' : '顯示'}
            </button>
          </div>

          {error && (
            <div className="px-4 py-3 bg-rose-100 text-rose-600 rounded-cell text-sm animate-slide-up">
              {error}
            </div>
          )}

          <Link
            to="/pin-login"
            className="block w-full text-center py-2.5 mb-1 bg-white border border-morandi-300 hover:bg-morandi-50 text-morandi-700 rounded-btn text-sm transition-all"
          >
            使用 PIN 快速登入
          </Link>

          <button
            type="submit"
            disabled={isLoading}
            className={`
              w-full py-3 rounded-btn font-medium text-white transition-all min-h-touch
              ${isLoading
                ? 'bg-morandi-300 cursor-wait'
                : 'bg-morandi-800 hover:bg-morandi-700 active:scale-[0.98]'
              }
            `}
          >
            {isLoading ? '登入中...' : '登入'}
          </button>

          {!showAdvanced && (
            <button
              type="button"
              onClick={() => setShowAdvanced(true)}
              className="w-full text-xs text-morandi-300 hover:text-morandi-500 py-1"
            >
              進階設定（多店鋪管理）
            </button>
          )}
        </form>

        <p className="text-center text-xs text-morandi-300 mt-6">
          製作給家人吃的食物，將員工視為家人
        </p>
      </div>
    </div>
  );
}
